import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing auto-dismissing messages (success, error, info, etc.)
 *
 * @param {number} dismissDelay - Time in milliseconds before auto-dismissing (default: 5000ms)
 * @returns {Object} - { message, setMessage, clearMessage }
 *
 * @example
 * const { message: successMsg, setMessage: setSuccessMsg } = useAutoDismissMessage(5000);
 *
 * // Set a message (will auto-clear after 5 seconds)
 * setSuccessMsg('Operation completed successfully!');
 *
 * // Display in JSX
 * {successMsg && <div className="alert alert-success">{successMsg}</div>}
 */
export const useAutoDismissMessage = (dismissDelay = 5000) => {
  const [message, setMessageState] = useState(null);
  const timeoutRef = useRef(null);

  // Clear any existing timeout when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Set message with auto-dismiss
  const setMessage = useCallback((msg) => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set the new message
    setMessageState(msg);

    // If message is not null/empty, schedule auto-dismiss
    if (msg) {
      timeoutRef.current = setTimeout(() => {
        setMessageState(null);
        timeoutRef.current = null;
      }, dismissDelay);
    }
  }, [dismissDelay]);

  // Manual clear function
  const clearMessage = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setMessageState(null);
  }, []);

  return { message, setMessage, clearMessage };
};

/**
 * Hook for managing multiple message types (success, error, info, warning)
 *
 * @param {number} dismissDelay - Time in milliseconds before auto-dismissing (default: 5000ms)
 * @returns {Object} - Message state and setters for each type
 *
 * @example
 * const { success, error, setSuccess, setError, clearAll } = useMessages(5000);
 *
 * // Set messages
 * setSuccess('Operation completed!');
 * setError('Something went wrong');
 *
 * // Display in JSX
 * {success && <div className="alert alert-success">{success}</div>}
 * {error && <div className="alert alert-danger">{error}</div>}
 */
export const useMessages = (dismissDelay = 5000) => {
  const { message: success, setMessage: setSuccess, clearMessage: clearSuccess } = useAutoDismissMessage(dismissDelay);
  const { message: error, setMessage: setError, clearMessage: clearError } = useAutoDismissMessage(dismissDelay);
  const { message: info, setMessage: setInfo, clearMessage: clearInfo } = useAutoDismissMessage(dismissDelay);
  const { message: warning, setMessage: setWarning, clearMessage: clearWarning } = useAutoDismissMessage(dismissDelay);

  const clearAll = useCallback(() => {
    clearSuccess();
    clearError();
    clearInfo();
    clearWarning();
  }, [clearSuccess, clearError, clearInfo, clearWarning]);

  return {
    success,
    error,
    info,
    warning,
    setSuccess,
    setError,
    setInfo,
    setWarning,
    clearSuccess,
    clearError,
    clearInfo,
    clearWarning,
    clearAll
  };
};

export default useAutoDismissMessage;
